import {
  Hidden,
  Paper,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import Pagination from '@material-ui/lab/Pagination'
import { useEffect, useMemo, useRef, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import { getTranslate } from '../../localization'
import CredentialPanel from './CredentialPanel'
import InventoryHealthColumnFilters from './InventoryHealthColumnFilters'
import InventoryHealthFilter from './InventoryHealthFilter'
import InventoryHealthHeader from './InventoryHealthHeader'
import InventoryHealthPMSEvidence from './InventoryHealthPMSEvidence'
import InventoryHealthTable from './InventoryHealthTable'
import InventoryHealthTableMobile from './InventoryHealthTableMobile'
import InventoryHealthVerdict from './InventoryHealthVerdict'
import { Option } from './SelectionFilters'
import { deriveChannelOptions } from './channelOptions'
import { verdictText } from './findings'
import useStyles from './styles/index.styles'
import {
  ColumnFilters,
  EMPTY_COLUMN_FILTERS,
  useComparisonRowFilters
} from './useComparisonRowFilters'
import { useInventoryHealthDiagnostic } from './useInventoryHealthDiagnostic'
import { useInventoryHealthQueryFilters } from './useInventoryHealthQueryFilters'
import { useInventoryHealthUrlState } from './useInventoryHealthUrlState'
import { usePMSEvidence } from './usePMSEvidence'

const ROWS_PER_PAGE = 50

/**
 * Inventory health diagnostic.
 *
 * Answers, for one property and date range: what does each of the PMS, CRS and
 * channel layers actually hold, can each channel authenticate at all, and is
 * the result healthy.
 *
 * The sibling Update tab reports AGE only. A property whose layers are minutes
 * old but disagree on price shows as Normal there, which is the case that
 * generates escalations — freshness is rarely the complaint.
 *
 * This file composes; it fetches nothing and renders no fields (§8.4). The
 * reads live in useInventoryHealthDiagnostic, the row narrowing in
 * useComparisonRowFilters, the shareable state in useInventoryHealthUrlState.
 */
export default function MonitoringInventoryHealth() {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { initial, write } = useInventoryHealthUrlState()
  const evidence = usePMSEvidence()
  const { result, loading, error, busy, run, triggerPull } =
    useInventoryHealthDiagnostic(evidence.load)

  const { filters, set, reset } = useInventoryHealthQueryFilters(initial)
  const { accommodation, provider, channel } = filters

  // Collapsed by default unless the URL already carries filter state (§3).
  const [filterOpen, setFilterOpen] = useState(
    Object.keys(initial).length === 0
  )
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({
    roomType: initial.roomType ?? '',
    ratePlan: initial.ratePlan ?? '',
    channelName: initial.channelName ?? '',
    assessment: initial.assessment ?? ''
  })
  const [page, setPage] = useState(1)

  const canSearch = accommodation != null && provider != null

  function shareableState(columns: ColumnFilters) {
    return {
      propertyCode: accommodation?.value,
      providerCode: provider?.value,
      channelID: channel?.value,
      from: filters.from,
      to: filters.to,
      differencesOnly: filters.differencesOnly,
      ...columns
    }
  }

  function search() {
    if (accommodation == null || provider == null) {
      return
    }

    setPage(1)
    write(shareableState(columnFilters))
    evidence.load(accommodation.value)
    run({
      propertyCode: accommodation.value,
      providerCode: provider.value,
      from: filters.from,
      to: filters.to,
      channelID: channel?.value,
      differencesOnly: filters.differencesOnly
    })
  }

  // A copied diagnostic URL is an instruction to restore the diagnostic, not
  // merely to remember which codes were used. Run it once after mount; the
  // code-only Options are sufficient for the typed query and are relabelled
  // when the picker queries resolve.
  const restored = useRef(false)
  useEffect(() => {
    if (
      !restored.current &&
      initial.propertyCode &&
      initial.providerCode &&
      accommodation?.value === initial.propertyCode &&
      provider?.value === initial.providerCode
    ) {
      restored.current = true
      search()
    }
    // This is a one-shot restoration of the mount snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accommodation?.value, provider?.value])

  function applyColumnFilters(next: ColumnFilters) {
    setColumnFilters(next)
    setPage(1)
    write(shareableState(next))
  }

  // The recovery offered when column filters exclude everything: the table can
  // say why it is empty, but only this level can undo it.
  function clearColumnFilters() {
    applyColumnFilters(EMPTY_COLUMN_FILTERS)
  }

  const channelOptions: Option[] = useMemo(
    () => deriveChannelOptions(result),
    [result]
  )
  const rows = useMemo(() => result?.rows ?? [], [result])
  const { filtered, options } = useComparisonRowFilters(rows, columnFilters)

  const pageCount = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE))
  // Clamped rather than reset. The result set also shrinks on paths this
  // component never sees — the diagnostic re-reads itself five seconds after a
  // PMS trigger, from inside its own hook — so a page held at 3 while the
  // result drops to one page rendered an empty table under a paginator still
  // claiming page 3. Every reset site can be forgotten; a clamp cannot.
  const safePage = Math.min(page, pageCount)
  const visible = filtered.slice(
    (safePage - 1) * ROWS_PER_PAGE,
    safePage * ROWS_PER_PAGE
  )

  return (
    <div>
      <PageHeader title={getTranslate('Inventory Health')} />

      <InventoryHealthHeader
        filterOpen={filterOpen}
        onToggleFilter={() => setFilterOpen((open) => !open)}
        onTriggerPull={() => triggerPull(accommodation?.value, provider?.value)}
        canTrigger={canSearch}
        busy={busy}
      />

      <InventoryHealthFilter
        open={filterOpen}
        filters={filters}
        set={set}
        channelOptions={channelOptions}
        onApply={search}
        onReset={reset}
        canApply={canSearch && !busy}
        applyLabel={getTranslate('Run diagnostic')}
      />

      {/*
        A live region has to be in the DOM before its content changes, or the
        mutation is never observed. Mounting it with the verdict announced the
        result of an expensive query to nobody, and a failed run was announced
        only by a toast that may have already gone.
      */}
      <div aria-live="polite" role="status" className={classes.srOnly}>
        {busy
          ? getTranslate('Running diagnostic')
          : error != null
          ? getTranslate('The diagnostic could not run.')
          : result != null
          ? verdictText(
              result.overallStatus,
              result.totalRows,
              result.differingRows
            )
          : ''}
      </div>

      {result == null && error == null && !busy && (
        <Paper className={classes.idle}>
          <Typography variant="body2" color="textSecondary">
            {getTranslate(
              'Select a property and a provider in the filter panel, then run the diagnostic. This queries the PMS directly and can take a few seconds.'
            )}
          </Typography>
        </Paper>
      )}

      {/*
        The skeleton only ever appeared on a RE-run, because the whole result
        block is gated on a result existing — so the expensive first run showed
        a 22px spinner and an otherwise unchanged screen, which is what the idle
        screen also looks like.
      */}
      {busy && result == null && (
        <Hidden smDown>
          <InventoryHealthTable
            rows={[]}
            loading
            onRefresh={search}
            loadedCount={0}
            onClearFilters={clearColumnFilters}
          />
        </Hidden>
      )}

      {error != null && (
        <Paper className={classes.section}>
          <Typography color="error">{error.message}</Typography>
        </Paper>
      )}

      {result != null && (
        <>
          <InventoryHealthVerdict result={result} />

          {/*
            Placed directly under the verdict and above the stored layers,
            because it is the only panel that can distinguish a stale number
            from one the PMS never sent — and that distinction changes how
            everything below it should be read.
          */}
          <InventoryHealthPMSEvidence
            logs={evidence.logs}
            rows={rows}
            loading={evidence.loading}
            failed={evidence.failed}
            providerURL={
              result.credentials.find(
                (entry) => entry.providerCode === provider?.value
              )?.providerURL ?? null
            }
          />
          <CredentialPanel providers={result.credentials} />

          <InventoryHealthColumnFilters
            filters={columnFilters}
            setFilters={applyColumnFilters}
            options={options}
            shown={filtered.length}
            total={rows.length}
          />

          <Hidden smDown>
            <InventoryHealthTable
              rows={visible}
              loading={loading}
              onRefresh={search}
              loadedCount={rows.length}
              onClearFilters={clearColumnFilters}
            />
          </Hidden>
          <Hidden mdUp>
            <InventoryHealthTableMobile
              rows={visible}
              loading={loading}
              onRefresh={search}
              loadedCount={rows.length}
              onClearFilters={clearColumnFilters}
            />
          </Hidden>

          {pageCount > 1 && (
            <div className={classes.paginationContainer}>
              <Pagination
                aria-label={getTranslate('Comparison pages')}
                count={pageCount}
                color="primary"
                size={isSmallScreen ? 'small' : 'medium'}
                page={safePage}
                onChange={(_, value) => setPage(value)}
              />
            </div>
          )}

          {rows.length < result.totalRows && (
            <Typography
              variant="caption"
              color="textSecondary"
              className={classes.truncationNote}>
              {getTranslate(
                'Showing {shown} of {total} comparisons. Narrow the date range or select a channel to see the rest.',
                { shown: rows.length, total: result.totalRows }
              )}
            </Typography>
          )}
        </>
      )}
    </div>
  )
}
